/** GxDeskTabs (DESIGN §3): the five desks plus Events, one shape, used by both faces. Each says where it stands with a state dot (none grey, some gold, all green); the count ("1 of 12 reviewed"; Events: how many cards are due) is the tooltip and the screen-reader text. */
import type { ContentCatalog } from "@bsim/contracts";
import type { DecisionFamily } from "@bsim/domain";
import { Icon } from "../theme/icons";
import { deskLabel, deskOrder } from "./labels";
import { GxTabs, type GxTab } from "../theme/GxTabs";

export type TabId = DecisionFamily | "events";
/** The one panel under the desk tabs: the active tab names it (`aria-controls`) and it names the tab (`aria-labelledby`). */
export const DESK_PANEL_ID = "decide-panel";
export const deskTabId = (tab: TabId): string => `${DESK_PANEL_ID}-tab-${tab}`;
type Tone = "none" | "some" | "all";

/** The desk's own line: how many of its open decisions are reviewed. A desk with none open says it follows the standing policy. */
export function deskLine(done: number, total: number): { readonly text: string; readonly tone: Tone } {
  if (total === 0) return { text: "Standing policy", tone: "none" };
  return { text: `${done} of ${total} reviewed`, tone: done === 0 ? "none" : done === total ? "all" : "some" };
}

export function GxDeskTabs({ catalog, active, holders, events, reviewed, onSelect }: {
  readonly catalog: ContentCatalog; readonly active: TabId; readonly holders: Readonly<Partial<Record<DecisionFamily, string>>>;
  /** Present when `pendingEvents` is non-empty: how many still need an answer, of how many. */
  readonly events: { readonly open: number; readonly total: number } | null; readonly onSelect: (tab: TabId) => void;
  /** Each desk's handled-of-open decision count. */
  readonly reviewed?: readonly { readonly desk: DecisionFamily; readonly adjusted: number; readonly maintained: number; readonly decisionIds: readonly unknown[] }[];
}) {
  const tabs: GxTab[] = deskOrder(catalog).map((family) => {
    const count = reviewed?.find((r) => r.desk === family);
    const holder = holders[family];
    const s = holder !== undefined ? { text: `${holder} editing`, tone: "some" as Tone } : deskLine((count?.adjusted ?? 0) + (count?.maintained ?? 0), count?.decisionIds.length ?? 0);
    return { id: family, label: deskLabel(catalog, family), testId: `decide.tab.${family}`, title: s.text, trail: <><i className={`gx-decide-dot gx-decide-dot--${s.tone}`} aria-hidden="true" /><span className="gx-sr">{s.text}</span></> };
  });
  if (events !== null) {
    const text = events.open > 0 ? `${events.open} due` : "All answered";
    tabs.push({ id: "events", label: "Events", testId: "decide.tab.events", title: text, lead: <Icon name="bolt" size={13} />, trail: <><i className={`gx-decide-dot gx-decide-dot--${events.open > 0 ? "some" : "all"}`} aria-hidden="true" /><span className="gx-sr">{text}</span></> });
  }
  return <GxTabs tabs={tabs} active={active} onSelect={(id) => onSelect(id as TabId)} label="Desks" panelId={DESK_PANEL_ID} testId="decide.families" />;
}
